import { useEffect, useState, useRef } from "react";
import { useAuth } from "@repo/ping-react-js";
import {
  fetchCredentialSources,
  CREDENTIAL_SECRET_TYPE_LABELS,
  type CredentialSource,
} from "../api/credentials";

export type CredentialEditModalProps = {
  source: string;
  saving: boolean;
  title: string;
  existingSources: string[];
  secretTypes: string[];
  saveError: string | null;
  onSave: (source: string, fields: Record<string, string>) => Promise<void>;
  onClose: () => void;
};

const CredentialEditModal = ({
  source,
  saving,
  title,
  existingSources,
  secretTypes,
  saveError,
  onSave,
  onClose,
}: CredentialEditModalProps) => {
  const { accessToken } = useAuth();
  const [sourceCode, setSourceCode] = useState(source);
  const [allSources, setAllSources] = useState<CredentialSource[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loadingSources, setLoadingSources] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [credentialType, setCredentialType] = useState<"api" | "up" | "cic" | "custom" | "">("");
  const [fields, setFields] = useState<{ key: string; value: string; custom?: boolean }[]>(() =>
    source && secretTypes.length > 0
      ? secretTypes.map((t) => ({ key: t, value: "" }))
      : [],
  );

  useEffect(() => {
    if (source) return;
    if (credentialType === "custom") {
      setFields([{ key: "", value: "", custom: true }]);
      return;
    }
    const keys: string[] =
      credentialType === "api" ? ["api_key"] :
      credentialType === "up" ? ["username", "password"] :
      credentialType === "cic" ? ["client_id", "client_secret"] :
      [];
    setFields(keys.map((k) => ({ key: k, value: "" })));
  }, [credentialType, source]);

  useEffect(() => {
    if (source || !accessToken) return;
    setLoadingSources(true);
    fetchCredentialSources(accessToken)
      .then((data) => setAllSources(data.sources.filter((s) => !existingSources.includes(s.code))))
      .catch(() => setAllSources([]))
      .finally(() => setLoadingSources(false));
  }, [source, accessToken, existingSources]);

  const filtered = searchText
    ? allSources.filter(
        (s) =>
          s.code.toLowerCase().includes(searchText.toLowerCase()) ||
          s.name.toLowerCase().includes(searchText.toLowerCase()),
      )
    : allSources;

  const handleSave = () => {
    const code = source || sourceCode.trim().toUpperCase();
    if (!code) return;
    const entries: Record<string, string> = {};
    for (const f of fields) {
      if (f.key && f.value) entries[f.key] = f.value;
    }
    if (Object.keys(entries).length === 0) return;
    onSave(code, entries);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-dialog-integrations" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{title}</h2>
        {!source && (
          <div className="creds-add-search-wrap">
            <input
              ref={inputRef}
              className="modal-input creds-add-search"
              type="text"
              value={searchText}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                setSearchText(val);
                setSourceCode(val);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder={loadingSources ? "Loading..." : "Search integrations..."}
              autoFocus
            />
            {showDropdown && (
              <div className="creds-add-list">
                {loadingSources ? (
                  <div className="creds-add-empty">Loading...</div>
                ) : filtered.length === 0 ? (
                  <div className="creds-add-empty">No results found</div>
                ) : (
                  filtered.map((s) => (
                    <button
                      key={s.code}
                      className="creds-add-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSourceCode(s.code);
                        setSearchText(s.code);
                        setShowDropdown(false);
                      }}
                    >
                      <span className="creds-add-item-label">
                        <strong>{s.code}</strong>
                        <span>{s.name}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
        <div className="creds-edit-fields">
          {!source && (
            <div className="creds-edit-field-row">
              <select
                className="modal-select creds-edit-key"
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value as "api" | "up" | "cic" | "custom" | "")}
              >
                <option value="">Select type...</option>
                <option value="custom">Custom</option>
                <option value="api">API Key</option>
                <option value="up">Username & Password</option>
                <option value="cic">Client ID & Client Secret</option>
              </select>
            </div>
          )}
          {fields.map((field, i) => (
            <div key={i} className="creds-edit-field-row">
              {field.custom ? (
                <input
                  className="modal-input creds-edit-key creds-custom-key"
                  type="text"
                  value={field.key}
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((f, idx) => (idx === i ? { ...f, key: e.target.value.toUpperCase() } : f)),
                    )
                  }
                  placeholder="Key"
                />
              ) : (
                <span className="creds-edit-key creds-edit-key-label">
                  {CREDENTIAL_SECRET_TYPE_LABELS[field.key] ?? field.key}
                </span>
              )}
              <input
                className="modal-input creds-edit-value"
                type="text"
                value={field.value}
                onChange={(e) =>
                  setFields((prev) =>
                    prev.map((f, idx) => (idx === i ? { ...f, value: e.target.value } : f)),
                  )
                }
                placeholder={field.custom ? "Value" : source ? "*********" : ""}
              />
            </div>
          ))}
        </div>
        {saveError && <p className="creds-status creds-error">{saveError}</p>}
        <div className="modal-actions">
          <button className="modal-btn" onClick={onClose}>Cancel</button>
          <button
            className="modal-btn primary"
            onClick={handleSave}
            disabled={
              saving ||
              (!source && !sourceCode.trim()) ||
              fields.length === 0 ||
              !fields.some((f) => f.key && f.value)
            }
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredentialEditModal;
